import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useOutletContext } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { ApiError, api, type Me } from '../lib/api.js';
import { hasCapability } from '../lib/access.js';
import { Button, EmptyState, Panel, useToast } from '../ui/index.js';

/**
 * Deleted tickets (067): what was deleted on this desk, by whom and why, and
 * Restore — which puts a ticket back exactly as it was. For whoever may
 * delete tickets here.
 */

interface Ctx {
  tenant: string;
  tenantName: string | undefined;
  me?: Me;
}

interface Row {
  number: number;
  subject: string;
  state: string;
  deletedAt: string;
  reason: string | null;
  deletedBy: string | null;
}

export function DeletedTickets() {
  const { tenant, tenantName, me } = useOutletContext<Ctx>();
  const qc = useQueryClient();
  const toast = useToast();
  const allowed = hasCapability(me, tenant, 'ticket.delete');
  const q = useQuery({
    queryKey: ['deleted-tickets', tenant],
    queryFn: () => api.get<{ tickets: Row[] }>(`/api/v1/tenants/${tenant}/tickets/deleted`),
    enabled: allowed,
  });
  const restore = useMutation({
    mutationFn: (n: number) => api.post(`/api/v1/tenants/${tenant}/tickets/${n}/restore`, {}),
    onSuccess: (_d, n) => {
      toast.success(`#${n} restored`, 'It is back in the queue exactly as it was.');
      void qc.invalidateQueries({ queryKey: ['deleted-tickets', tenant] });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (e) => toast.error('Not restored', e instanceof ApiError ? e.message : 'the restore failed'),
  });

  if (!allowed) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-6">
        <EmptyState title="Not available" hint="Deleted tickets are for people who may delete tickets on this desk." />
      </div>
    );
  }

  const rows = q.data?.tickets ?? [];
  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <p className="mb-3 text-2xs text-ink-tertiary">
        <Link to="/queue" className="hover:text-ink-secondary">
          ← Queue
        </Link>
      </p>
      <Panel
        title={`Deleted tickets${tenantName ? ` · ${tenantName}` : ''}`}
        subtitle="Hidden from the queue, search and reports. Restore puts one back exactly as it was."
      >
        {q.isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
        {q.data && rows.length === 0 && <p className="text-xs text-ink-tertiary">Nothing has been deleted on this desk.</p>}
        <ul className="divide-y divide-line-subtle">
          {rows.map((r) => (
            <li key={r.number} className="flex flex-wrap items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-ink">
                  <span className="nums text-ink-tertiary">#{r.number}</span> {r.subject}
                </div>
                <div className="text-2xs text-ink-tertiary">
                  Deleted {new Date(r.deletedAt).toLocaleString()}
                  {r.deletedBy ? ` by ${r.deletedBy}` : ''}
                  {r.reason ? ` — “${r.reason}”` : ''}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                leading={<RotateCcw />}
                loading={restore.isPending && restore.variables === r.number}
                onClick={() => restore.mutate(r.number)}
              >
                Restore
              </Button>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
